/**
 * A capability link (an invite code, a share token) admits whoever holds it,
 * so the path segment carrying it is as sensitive as a password (policy §11).
 * An app declares those routes once, and a pathname matching one is logged as
 * the route with each `:param` written `[param]`: with `/invite/:code`
 * declared, `/invite/abc123` logs as `/invite/[code]`.
 *
 * A route matches the way React Router's does: on the percent-decoded path,
 * literal segments exactly but in any case, each `:param` one non-empty
 * segment, any number of trailing slashes, and nothing after. A path that only
 * shares a prefix is left alone.
 */

export interface RedactedRoute {
  readonly pattern: RegExp;
  readonly label: string;
}

const PARAM_SEGMENT = /^:(\w+)$/;

const escapeRegExp = (literal: string): string =>
  literal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function invalidRoute(route: string, reason: string): TypeError {
  return new TypeError(`Redacted route ${JSON.stringify(route)} ${reason}`);
}

/**
 * Throws on a route it cannot read rather than skipping it: a skipped route
 * logs its token, and the list is static, so the first dev load finds it.
 */
function compileRoute(route: string): RedactedRoute {
  if (!route.startsWith("/")) throw invalidRoute(route, "must start with /");
  const segments = route.replace(/\/$/, "").split("/").slice(1);

  const sources: string[] = [];
  const labels: string[] = [];
  for (const segment of segments) {
    const param = PARAM_SEGMENT.exec(segment)?.[1];
    if (param !== undefined) {
      sources.push("[^/]+");
      labels.push(`[${param}]`);
    } else if (segment === "" || /[:*?]/.test(segment)) {
      // No wildcards or optional params: a pattern that matched more than
      // its own shape would rewrite paths nobody declared.
      throw invalidRoute(route, `has an unsupported segment "${segment}"`);
    } else {
      sources.push(escapeRegExp(segment));
      labels.push(segment);
    }
  }

  return {
    pattern: new RegExp(`^/${sources.join("/")}/*$`, "i"),
    label: `/${labels.join("/")}`,
  };
}

/** Compile the routes once, for `redactPath` to test per call. */
export function compileRedactedRoutes(
  routes: readonly string[],
): readonly RedactedRoute[] {
  return routes.map(compileRoute);
}

// As the router decodes before it matches: a token behind `/%69nvite/` still
// renders the invite page. An encoded slash stays one segment.
function decodedPath(pathname: string): string {
  return pathname
    .split("/")
    .map((segment) => {
      try {
        return decodeURIComponent(segment).replace(/\//g, "%2F");
      } catch {
        return segment;
      }
    })
    .join("/");
}

/** `pathname`, or the label of the first route it matches. */
export function redactPath(
  pathname: string,
  routes: readonly RedactedRoute[],
): string {
  if (routes.length === 0) return pathname;
  const decoded = decodedPath(pathname);
  return routes.find(({ pattern }) => pattern.test(decoded))?.label ?? pathname;
}
